import React from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import StairsIcon from '@mui/icons-material/Stairs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useListingHeaderLogic } from '../hooks/useListingHeaderLogic';
import { Buttons } from '../../../../constants/Buttons';
import { FeatureFlags } from '../../../../flags';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { PropertyField } from '../../../../system/field';

interface DesktopHeaderProps {
  logic: ReturnType<typeof useListingHeaderLogic>;
  handleGroupClick: (
    groupName: string,
    fieldKey?: string,
    isMobile?: boolean,
  ) => void;
}

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  logic,
  handleGroupClick,
}) => {
  const { hasFeature } = useFeatureFlags();
  const {
    hasErrors,
    anchorEl,
    autosaved,
    autosaveStatus,
    autosaveError,
    saveDraftStatus,
    saveDraftError,
    address,
    formattedStatus,
    formattedPrice,
    formattedExpirationDate,
    beds,
    baths,
    stories,
    listPicture,
    isReturnToLegacy,
    hasReturnToLegacy,
    isAbleToPublish,
    handleReturnToLegacy,
    handleActionsClick,
    handleActionsClose,
    handleSaveDraft,
    handlePreviewDraft,
    handleShareDraft,
    handlePublish,
    handleNavigateBack,
    propertyType,
    latitude,
    longitude,
    isResidental,
  } = logic;

  const showPublishActions = hasFeature(FeatureFlags.NGW_PUBLISH);
  const disablePublishButton = !isAbleToPublish();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 2,
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton sx={{ mr: 2 }} onClick={() => handleNavigateBack()}>
          <ArrowBackIcon sx={{ color: '#F13C00' }} />
        </IconButton>
        <Box>
          <Avatar
            sx={{ width: '89px', height: '89px', mr: '15px', borderRadius: 2 }}
            variant="square"
            {...(listPicture
              ? { src: listPicture }
              : {
                  src: `/property_type/${propertyType}.svg`,
                  imgProps: { style: { objectFit: 'contain' } },
                })}
          ></Avatar>
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '26px' }}>
            {address}
          </Typography>
          <Box display="flex" gap={1}>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
              fontWeight={700}
            >
              Latitude:
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              {latitude}
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
              fontWeight={700}
            >
              Longitude:
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              {longitude}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0 }}>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <BedIcon fontSize="small" /> {beds}
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <BathtubIcon fontSize="small" /> {baths}
            </Typography>
            <Typography
              variant="body2"
              display="flex"
              alignItems="center"
              sx={{
                color: 'grey.500',
              }}
            >
              <StairsIcon fontSize="small" /> {stories}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                flex: 1,
                gap: 2,
              }}
            >
              <Typography variant="body2">
                Price:{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: 'secondary.medium',
                  }}
                  onClick={() =>
                    handleGroupClick(
                      'Listing Details', // where can I get this from?
                      PropertyField.LIST_PRICE,
                    )
                  }
                >
                  {formattedPrice}
                </Typography>
              </Typography>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Expires:{' '}
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    color: 'secondary.medium',
                  }}
                  onClick={() =>
                    handleGroupClick(
                      'Listing Agreement', // where can I get this from?
                      PropertyField.EXPIRATION_DATE,
                    )
                  }
                >
                  {formattedExpirationDate}
                </Typography>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'secondary.medium',
              }}
              onClick={() =>
                handleGroupClick(
                  'Listing Details', // where can I get this from?
                  PropertyField.MLS_STATUS,
                )
              }
            >
              {formattedStatus}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mt: 2.5,
          }}
        >
          {hasReturnToLegacy && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                marginRight: {
                  md: '0', // Original margin on desktop
                  lg: showPublishActions ? '50px' : '20px',
                },
              }}
            >
              <Switch
                checked={isReturnToLegacy}
                onChange={(event) => handleReturnToLegacy()}
              />
              <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                Return to legacy
              </Typography>
            </Box>
          )}

          {showPublishActions ? (
            <>
              <Button
                onClick={handleActionsClick}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  color: '#F13C00',
                  mr: 2,
                  textTransform: 'none',
                }}
              >
                Listing Actions
                {saveDraftStatus === 'loading' && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )}
                {saveDraftStatus === 'error' && (
                  <Tooltip title={saveDraftError || 'Error saving draft'}>
                    <ErrorIcon sx={{ color: 'error.main', ml: 1 }} />
                  </Tooltip>
                )}
                {saveDraftStatus === 'success' && (
                  <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                )}
              </Button>

              <Button
                variant="contained"
                sx={Buttons.primary}
                disabled={hasErrors || disablePublishButton}
                onClick={handlePublish}
              >
                Publish
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              disabled={saveDraftStatus === 'loading'}
              sx={Buttons.primary}
              onClick={handleSaveDraft}
            >
              {saveDraftStatus === 'loading' && (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              )}
              Save Draft
            </Button>
          )}
        </Box>

        {autosaved && (
          <Box
            mt={2}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end', // Right align the content
              paddingRight: '16px', // Match the padding of the button container
            }}
          >
            <Typography
              variant="body2"
              component="div"
              color={autosaveStatus === 'failed' ? 'error' : 'text.secondary'}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {autosaveStatus === 'success' || autosaveStatus === 'loading' ? (
                <CheckCircleIcon sx={{ color: '#477E39', mr: 1 }} />
              ) : (
                <Tooltip title={autosaveError || 'Error autosaving'}>
                  <ErrorIcon sx={{ mr: 1 }} />
                </Tooltip>
              )}
              Autosave{autosaveStatus === 'success' ? 'd' : ''}{' '}
              {autosaveStatus === 'success' ? '' : 'failed'} at {autosaved}
            </Typography>
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionsClose}
      >
        <MenuItem onClick={handleSaveDraft}>Save Draft</MenuItem>
        {isResidental && hasFeature(FeatureFlags.NGW_PREVIEW_DRAFT) && (
          <MenuItem onClick={handlePreviewDraft}>Preview Draft</MenuItem>
        )}
        {isResidental && hasFeature(FeatureFlags.NGW_SHARE_DRAFT) && (
          <MenuItem onClick={handleShareDraft}>Share Draft</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default DesktopHeader;
